// src/pages/LandingPage.js
import React, { useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { AuthContext } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { db } from '../firebase';

const LandingPage = () => {
  const { currentUser, userType } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Logged-in users should not see the public landing page.
    const redirectAuthedUsers = async () => {
      if (!currentUser) return;

      // Company users always go to dashboard
      if (userType === 'company') {
        navigate('/company/dashboard', { replace: true });
        return;
      }

      // Student users: if in an active project, go to dashboard; otherwise browse projects
      if (userType === 'student') {
        try {
          const applicationsQuery = query(
            collection(db, 'applications'),
            where('studentId', '==', currentUser.uid),
            orderBy('appliedAt', 'desc'),
            limit(10)
          );

          const applicationsSnapshot = await getDocs(applicationsQuery);
          const applications = applicationsSnapshot.docs.map((d) => ({
            id: d.id,
            ...d.data()
          }));

          const acceptedApps = applications.filter((a) => a.status === 'accepted' && a.projectId);

          for (const app of acceptedApps) {
            const projectSnap = await getDoc(doc(db, 'projects', app.projectId));
            if (projectSnap.exists()) {
              const projectData = projectSnap.data();
              if (projectData?.enrolledStudents?.includes(currentUser.uid)) {
                navigate('/student/dashboard', { replace: true });
                return;
              }
            }
          }

          navigate('/projects', { replace: true });
        } catch (e) {
          // If anything fails, default to browse projects to keep the flow simple.
          navigate('/projects', { replace: true });
        }
        return;
      }

      // Fallback for unknown user type
      navigate('/projects', { replace: true });
    };

    redirectAuthedUsers();

    // Handle scroll to section when navigating from other pages
    if (!currentUser && location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [currentUser, userType, navigate, location.state]);

  // Never show the public landing page to logged-in users.
  if (currentUser) return null;

  return (
    <>
      <Navigation />

      {/* Hero Section */}
      <section className="hero" style={{
        padding: '60px 0',
        backgroundColor: 'white',
        position: 'relative'
      }}>
        <div className="container" style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: '0 40px',
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: '80px'
        }}>
          <div style={{ flex: '1', maxWidth: '600px' }}>
            <div style={{
              display: 'inline-block',
              backgroundColor: '#e0e7ff',
              color: '#6366f1',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '32px'
            }}>
              Simple Student–Company Project Portal
            </div>
            <h1 style={{
              fontSize: '42px',
              fontWeight: '800',
              color: '#1a1a1a',
              marginBottom: '24px',
              lineHeight: '1.1'
            }}>
              Where Talent Meets Opportunity
            </h1>
            <p style={{
              fontSize: '18px',
              color: '#6b7280',
              lineHeight: '1.6',
              marginBottom: '40px'
            }}>
              Post real projects, let students apply, and work together in one place.
              Build portfolios, gain experience, and create real business value without any complexity.
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <Link 
                to="/register?type=company" 
                style={{
                  backgroundColor: '#6366f1',
                  color: 'white',
                  padding: '16px 32px',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '16px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#5854eb';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#6366f1';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                For Companies
              </Link>
              <Link 
                to="/register?type=student" 
                style={{
                  backgroundColor: 'white',
                  color: '#6366f1',
                  padding: '16px 32px',
                  borderRadius: '12px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '16px',
                  border: '2px solid #6366f1',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#6366f1';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = '#6366f1';
                }}
              >
                For Students
              </Link>
            </div>
          </div>
          <div style={{ flex: '1', display: 'flex', justifyContent: 'center' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              {/* Colorful figure illustrations */}
              <div style={{
                width: '80px',
                height: '120px',
                backgroundColor: '#6366f1',
                borderRadius: '40px 40px 8px 8px',
                position: 'relative'
              }}>
                <div style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: '#6366f1',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '-15px',
                  left: '50%',
                  transform: 'translateX(-50%)'
                }}></div>
              </div>
              <div style={{
                width: '80px',
                height: '140px',
                backgroundColor: '#ec4899',
                borderRadius: '40px 40px 8px 8px',
                position: 'relative'
              }}>
                <div style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: '#ec4899',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '-15px',
                  left: '50%',
                  transform: 'translateX(-50%)'
                }}></div>
              </div>
              <div style={{
                width: '80px',
                height: '100px',
                backgroundColor: '#06b6d4',
                borderRadius: '40px 40px 8px 8px',
                position: 'relative'
              }}>
                <div style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: '#06b6d4',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '-15px',
                  left: '50%',
                  transform: 'translateX(-50%)'
                }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features" style={{
        padding: '50px 0',
        backgroundColor: '#f8fafc'
      }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 40px' }}>
          <div className="section-header" style={{ textAlign: 'center', marginBottom: '80px' }}>
            <div style={{
              display: 'inline-block',
              backgroundColor: '#e0e7ff',
              color: '#6366f1',
              padding: '8px 16px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '24px'
            }}>
              Benefits
            </div>
            <h2 style={{
              fontSize: '36px',
              fontWeight: '800',
              color: '#1a1a1a',
              marginBottom: '24px',
              lineHeight: '1.1'
            }}>
              Transforming Work Experience
            </h2>
            <p style={{
              fontSize: '18px',
              color: '#6b7280',
              lineHeight: '1.6',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              Our platform bridges the gap between academic learning and
              professional experience.
            </p>
          </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: '40px'
            }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '40px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ marginBottom: '24px' }}>
                <img 
                  src="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                  alt="For Companies" 
                  style={{ 
                    width: '100%', 
                    height: '140px', 
                    objectFit: 'cover', 
                    borderRadius: '12px' 
                  }}
                />
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1a1a1a',
                marginBottom: '16px'
              }}>
                For Companies
              </h3>
              <p style={{
                color: '#6b7280',
                lineHeight: '1.6',
                fontSize: '14px'
              }}>
                Tap into a pool of motivated students with fresh perspectives.
                Complete projects affordably while discovering future talent for
                your organization.
              </p>
            </div>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '40px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ marginBottom: '24px' }}>
                <img 
                  src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                  alt="For Students" 
                  style={{ 
                    width: '100%', 
                    height: '140px', 
                    objectFit: 'cover', 
                    borderRadius: '12px' 
                  }}
                />
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1a1a1a',
                marginBottom: '16px'
              }}>
                For Students
              </h3>
              <p style={{
                color: '#6b7280',
                lineHeight: '1.6',
                fontSize: '14px'
              }}>
                Build a standout portfolio with real-world projects. Gain
                valuable experience, develop professional skills, and make
                meaningful connections.
              </p>
            </div>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '40px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ marginBottom: '24px' }}>
                <img 
                  src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                  alt="Simple Workflow" 
                  style={{ 
                    width: '100%', 
                    height: '140px', 
                    objectFit: 'cover', 
                    borderRadius: '12px' 
                  }}
                />
              </div>
              <h3 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#1a1a1a',
                marginBottom: '16px'
              }}>
                Straightforward Process
              </h3>
              <p style={{
                color: '#6b7280',
                lineHeight: '1.6',
                fontSize: '14px'
              }}>
                Companies post projects and review applications, and students pick
                opportunities that fit their interests and schedule. No algorithms,
                just clear projects and simple collaboration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works" id="how-it-works" style={{
        padding: '50px 0',
        backgroundColor: 'white'
      }}>
        <div className="container">
          <div className="section-header">
            <div className="feature-badge">Process</div>
            <h2>How It Works</h2>
            <p>Simple steps to start collaborating</p>
          </div>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Create Profile</h3>
              <p>
                Companies post projects and requirements. Students create
                detailed profiles showcasing their skills and interests.
              </p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Apply & Review</h3>
              <p>
                Students browse open projects and apply. Companies read
                applications and decide who they’d like to work with.
              </p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Collaborate</h3>
              <p>
                Work together through our platform with integrated tools for
                smooth communication and project management.
              </p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h3>Succeed Together</h3>
              <p>
                Companies get valuable work done while students gain real
                experience. Win-win!
              </p>
            </div>
            <div className="step-line"></div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials" id="testimonials" style={{
        padding: '50px 0',
        backgroundColor: '#f8fafc'
      }}>
        <div className="container">
          <div className="section-header">
            <div className="feature-badge">Testimonials</div>
            <h2>Success Stories</h2>
            <p>Real experiences from our community of companies and students</p>
          </div>
          <div className="testimonial-grid">
            <div className="testimonial-card">
              <p className="testimonial-text">
                "We needed help with our digital marketing strategy but couldn't
                afford an agency. The marketing students we connected with
                delivered incredible value and fresh ideas that transformed our
                approach."
              </p>
              <div className="testimonial-author">
                <div className="author-image">SJ</div>
                <div className="author-details">
                  <h4>Sarah Johnson</h4>
                  <p>Founder, EcoTech Solutions</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <p className="testimonial-text">
                "The web development project I completed through Synked became
                the centerpiece of my portfolio. It directly led to my
                internship at a major tech company and gave me confidence in my
                abilities."
              </p>
              <div className="testimonial-author">
                <div className="author-image">JK</div>
                <div className="author-details">
                  <h4>Jason Kim</h4>
                  <p>Computer Science Student</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <p className="testimonial-text">
                "Our startup saved thousands in development costs while
                providing valuable experience to talented students. The quality
                of work exceeded our expectations, and we've even hired two
                students full-time!"
              </p>
              <div className="testimonial-author">
                <div className="author-image">MR</div>
                <div className="author-details">
                  <h4>Michael Rodriguez</h4>
                  <p>CTO, FinNext Solutions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta" style={{
        padding: '50px 0',
        backgroundColor: '#6366f1'
      }}>
        <div className="container">
          <h2>Ready to Bridge the Gap?</h2>
          <p>
            Join our community today and start connecting with opportunities
            that transform careers and businesses.
          </p>
          <div className="cta-buttons">
            <Link 
              to={
                !currentUser 
                  ? '/register?type=company' 
                  : userType === 'company' 
                    ? '/post-project' 
                    : '/register?type=company'  // Student trying to post project should register as company
              } 
              className="btn btn-white"
              onClick={() => setTimeout(() => window.scrollTo(0, 0), 100)}
            >
              Post a Project
            </Link>
            <Link 
              to={
                !currentUser 
                  ? '/register?type=student' 
                  : userType === 'student' 
                    ? '/student/dashboard' 
                    : '/register?type=student'  // Company trying to join as student should register as student
              } 
              className="btn btn-white-outline"
            >
              Join as a Student
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default LandingPage;




